import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { WS_ORIGIN } from '../../config';
import {
  getBearerTokenForApi,
  getStoredPoolSitePoolId,
  getStoredPoolSiteToken,
} from '../../utils/poolSiteAuth';
import { forceStopPoolAlertDevices } from '../../services/poolAlertDeviceService';

export type PoolSiteSafetyAlertPayload = {
  type?: string;
  level?: string;
  pool_id?: number | string;
  pool_name?: string;
  message?: string;
  duration_sec?: number;
  timestamp?: string;
  /** 관제 이벤트 캡처 (data URL) */
  image_data_url?: string;
  location?: string;
  risk_type?: string;
  /** true면 닫기(수동·자동) 시 경광/부저 force-stop */
  stop_devices_on_close?: boolean;
};

type Props = {
  /** 수신할 시설 ID (없으면 연결하지 않음) */
  poolId: number | string | null | undefined;
  /** 기본 auto-close 초 (페이로드 duration_sec 우선) */
  defaultDurationSec?: number;
};

const DEFAULT_DURATION_SEC = 15;
/** AI 관제 이벤트(events) — 확인 시간 여유 */
const EVENTS_DEFAULT_DURATION_SEC = 60;

function mapRiskLevelToAlert(raw: unknown): 'danger' | 'warning' | null {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (
    s === 'danger' ||
    s === 'critical' ||
    s.includes('위험') ||
    s.includes('심각')
  ) {
    return 'danger';
  }
  if (s === 'warning' || s.includes('주의') || s.includes('경고') || s.includes('경보')) {
    return 'warning';
  }
  return null;
}

function coercePoolId(raw: unknown, fallback: string): string | number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return fallback;
}

function messageFromEventDetails(details: unknown, fallback: string): string {
  if (details == null) return fallback;
  let obj: Record<string, unknown> | null = null;
  if (typeof details === 'string') {
    try {
      const parsed = JSON.parse(details) as unknown;
      if (parsed && typeof parsed === 'object') obj = parsed as Record<string, unknown>;
    } catch {
      return details.trim() || fallback;
    }
  } else if (typeof details === 'object') {
    obj = details as Record<string, unknown>;
  }
  if (!obj) return fallback;
  for (const key of ['message', 'description', 'summary', 'title']) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return fallback;
}

/**
 * WS 원문 → 시설 팝업 payload.
 * - safety_alert: 관제 「메시지 전송」/자동 알림
 * - events: AI·수동 관제 이벤트 (풀 WS 브로드캐스트)
 */
export function normalizePoolSiteAlertMessage(
  message: Record<string, unknown>,
  expectPoolId: string
): PoolSiteSafetyAlertPayload | null {
  const type = String(message.type || '').toLowerCase();
  const nested =
    message.data && typeof message.data === 'object'
      ? (message.data as Record<string, unknown>)
      : null;

  if (type === 'events' || type === 'event') {
    const data = nested || message;
    const level = mapRiskLevelToAlert(data.risk_level ?? data.level);
    if (!level) return null;
    const msgPoolId = data.pool_id != null ? String(data.pool_id) : expectPoolId;
    if (msgPoolId && msgPoolId !== expectPoolId) return null;
    const eventType = String(data.event_type || data.risk_type || 'risk');
    const text = messageFromEventDetails(
      data.details,
      `${eventType} 위험 이벤트가 감지됨. 즉시 확인`
    );
    const imageRaw = data.image_data_url || data.snapshot_data_url;
    const image =
      typeof imageRaw === 'string' && imageRaw.startsWith('data:image/')
        ? imageRaw
        : undefined;
    return {
      type: 'events',
      level,
      pool_id: coercePoolId(data.pool_id, expectPoolId),
      pool_name: typeof data.pool_name === 'string' ? data.pool_name : undefined,
      message: text,
      location: typeof data.location === 'string' ? data.location : undefined,
      risk_type: eventType,
      image_data_url: image,
      duration_sec: EVENTS_DEFAULT_DURATION_SEC,
      stop_devices_on_close: true,
    };
  }

  const body: Record<string, unknown> = nested
    ? { ...nested, type: nested.type || message.type }
    : message;
  const bodyType = String(body.type || type || '').toLowerCase();
  const levelRaw = String(body.level || '').toLowerCase();
  const mapped = mapRiskLevelToAlert(body.level) || mapRiskLevelToAlert(body.risk_level);
  const isSafetyType =
    bodyType === 'safety_alert' ||
    type === 'safety_alert' ||
    levelRaw === 'danger' ||
    levelRaw === 'warning' ||
    mapped != null;
  if (!isSafetyType) return null;

  const msgPoolId = body.pool_id != null ? String(body.pool_id) : expectPoolId;
  if (msgPoolId && msgPoolId !== expectPoolId) return null;

  const level =
    mapped ||
    (levelRaw === 'danger' || levelRaw === 'warning' ? (levelRaw as 'danger' | 'warning') : 'danger');

  return {
    type: 'safety_alert',
    level,
    pool_id: coercePoolId(body.pool_id, expectPoolId),
    pool_name: typeof body.pool_name === 'string' ? body.pool_name : undefined,
    message: typeof body.message === 'string' ? body.message : undefined,
    duration_sec:
      typeof body.duration_sec === 'number'
        ? body.duration_sec
        : Number(body.duration_sec) || undefined,
    timestamp: typeof body.timestamp === 'string' ? body.timestamp : undefined,
    image_data_url:
      typeof body.image_data_url === 'string' ? body.image_data_url : undefined,
    location: typeof body.location === 'string' ? body.location : undefined,
    risk_type: typeof body.risk_type === 'string' ? body.risk_type : undefined,
    stop_devices_on_close:
      body.stop_devices_on_close === false ? false : true,
  };
}

/**
 * 시설 PC — safety_alert·관제 events 를 받아 위험 알림 팝업 표시.
 * 닫기 시 경광/부저 중지(stop_devices_on_close).
 */
const PoolSiteSafetyAlertDialog: React.FC<Props> = ({
  poolId,
  defaultDurationSec = DEFAULT_DURATION_SEC,
}) => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<PoolSiteSafetyAlertPayload | null>(null);
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const stoppingRef = useRef(false);
  const payloadRef = useRef<PoolSiteSafetyAlertPayload | null>(null);

  const clearTimers = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  const stopAlertDevicesIfNeeded = useCallback(
    (msg: PoolSiteSafetyAlertPayload | null) => {
      if (!msg) return;
      if (msg.stop_devices_on_close === false) return;
      const pid = Number(msg.pool_id ?? poolId);
      if (!Number.isFinite(pid) || pid <= 0) return;
      if (stoppingRef.current) return;
      stoppingRef.current = true;
      void Promise.all([
        forceStopPoolAlertDevices(pid, 'siren', { silentError: true }).catch(() => undefined),
        forceStopPoolAlertDevices(pid, 'buzzer', { silentError: true }).catch(() => undefined),
      ]).finally(() => {
        stoppingRef.current = false;
      });
    },
    [poolId]
  );

  const handleClose = useCallback(() => {
    clearTimers();
    const snap = payloadRef.current;
    payloadRef.current = null;
    setOpen(false);
    setPayload(null);
    setRemainingSec(null);
    stopAlertDevicesIfNeeded(snap);
  }, [clearTimers, stopAlertDevicesIfNeeded]);

  const showAlert = useCallback(
    (msg: PoolSiteSafetyAlertPayload) => {
      const level = String(msg.level || '').toLowerCase();
      if (level && level !== 'danger' && level !== 'warning') {
        return;
      }
      // 전송 직후: safety_alert(캡처 있음) → events(캡처 없음) 순으로 오면
      // 사진이 사라짐. 같은 시설·사진 없는 events 는 기존 캡처를 유지.
      const prev = payloadRef.current;
      const nextType = String(msg.type || '').toLowerCase();
      const prevType = String(prev?.type || '').toLowerCase();
      const prevImg = (prev?.image_data_url || '').trim();
      const nextImg = (msg.image_data_url || '').trim();
      const samePool =
        prev != null &&
        String(prev.pool_id ?? '') === String(msg.pool_id ?? '') &&
        String(prev.pool_id ?? '') !== '';
      let merged = msg;
      if (
        samePool &&
        prevImg &&
        !nextImg &&
        (nextType === 'events' || nextType === 'event') &&
        (prevType === 'safety_alert' || Boolean(prevImg))
      ) {
        merged = { ...msg, image_data_url: prevImg };
      }

      const durRaw = Number(merged.duration_sec);
      const durationSec = Math.min(
        300,
        Math.max(
          3,
          Number.isFinite(durRaw) && durRaw > 0
            ? Math.floor(durRaw)
            : Math.floor(defaultDurationSec) || DEFAULT_DURATION_SEC
        )
      );

      clearTimers();
      payloadRef.current = merged;
      setPayload(merged);
      setOpen(true);
      setRemainingSec(durationSec);

      closeTimerRef.current = setTimeout(() => {
        handleClose();
      }, durationSec * 1000);

      tickTimerRef.current = setInterval(() => {
        setRemainingSec((prev) => {
          if (prev == null || prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);
    },
    [clearTimers, defaultDurationSec, handleClose]
  );

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    const pid = poolId != null ? String(poolId).trim() : '';
    if (!pid || !Number.isFinite(Number(pid)) || Number(pid) <= 0) {
      return undefined;
    }

    let cancelled = false;

    const disconnect = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close(1000, 'unmount');
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };

    const connect = () => {
      if (cancelled) return;
      const storedPid = getStoredPoolSitePoolId();
      const siteToken = getStoredPoolSiteToken();
      // 이 시설 현장 토큰이 있으면 우선 — 관리자 JWT만 쓰면 만료·권한 문제로 끊길 수 있음
      const token =
        siteToken && storedPid != null && String(storedPid) === pid
          ? siteToken
          : getBearerTokenForApi();
      if (!token) {
        reconnectTimerRef.current = setTimeout(connect, 5000);
        return;
      }
      // /ws/pool — nginx location /ws 로 백엔드 전달 ( /pool 은 SPA 로 갈 수 있음 )
      const url = `${WS_ORIGIN}/ws/pool/${encodeURIComponent(pid)}?token=${encodeURIComponent(token)}`;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
          if (event.data === 'pong' || event.data === 'ping') {
            if (event.data === 'ping') {
              try {
                ws.send('pong');
              } catch {
                /* ignore */
              }
            }
            return;
          }
          try {
            const message = JSON.parse(event.data as string) as Record<string, unknown>;
            const body = normalizePoolSiteAlertMessage(message, pid);
            if (!body) return;
            showAlert(body);
          } catch {
            /* ignore non-json */
          }
        };

        ws.onclose = () => {
          wsRef.current = null;
          if (cancelled) return;
          const attempt = reconnectAttemptsRef.current + 1;
          reconnectAttemptsRef.current = attempt;
          const delay = Math.min(30000, 1500 * Math.pow(1.5, Math.min(attempt, 8)));
          reconnectTimerRef.current = setTimeout(connect, delay);
        };

        ws.onerror = () => {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        };
      } catch {
        reconnectTimerRef.current = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      cancelled = true;
      disconnect();
    };
  }, [poolId, showAlert]);

  if (!payload && !open) return null;

  const isDanger = String(payload?.level || 'danger').toLowerCase() === 'danger';
  const isAiEvent =
    String(payload?.type || '').toLowerCase() === 'events' ||
    String(payload?.type || '').toLowerCase() === 'event';
  const title = isAiEvent
    ? isDanger
      ? '관제 위험 이벤트'
      : '관제 경고 이벤트'
    : isDanger
      ? '위험 알림'
      : '경고 알림';
  const imageUrl = (payload?.image_data_url || '').trim();
  const metaBits = [payload?.location, payload?.risk_type].filter(Boolean).join(' · ');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          border: isDanger ? '3px solid #d32f2f' : '3px solid #ed6c02',
          bgcolor: isDanger ? '#1a0000' : '#1a1000',
          color: '#fff',
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 50 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#fff' }}>
        <WarningAmberIcon color={isDanger ? 'error' : 'warning'} fontSize="large" />
        {title}
        {payload?.pool_name ? ` — ${payload.pool_name}` : null}
      </DialogTitle>
      <DialogContent>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt="이벤트 캡처"
            sx={{
              width: '100%',
              maxHeight: 360,
              objectFit: 'contain',
              borderRadius: 1,
              mb: 1.5,
              bgcolor: '#000',
            }}
          />
        ) : null}
        {metaBits ? (
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.85 }}>
            {metaBits}
          </Typography>
        ) : null}
        <Typography variant="h6" sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {payload?.message || '현장에 위험 알림이 도착함. 즉시 확인'}
        </Typography>
        {remainingSec != null && remainingSec > 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {remainingSec}초 후 자동으로 닫히며 경광등/부저도 중지.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="contained" color={isDanger ? 'error' : 'warning'} onClick={handleClose}>
          닫기 (경광/부저 중지)
        </Button>
      </DialogActions>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          animation: isDanger ? 'poolSiteAlertPulse 1.2s ease-in-out infinite' : 'none',
          '@keyframes poolSiteAlertPulse': {
            '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(211,47,47,0)' },
            '50%': { boxShadow: 'inset 0 0 40px 8px rgba(211,47,47,0.35)' },
          },
        }}
      />
    </Dialog>
  );
};

export default PoolSiteSafetyAlertDialog;
